import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader2,
  Briefcase,
  MapPin,
  AlertCircle,
  Check,
  X,
  Camera,
  Plus,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  FileText,
  CheckSquare,
  Image as ImageIcon,
  MessageSquare,
  ChevronRight,
  GripVertical,
  Eye,
  Download,
  Share2
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';
import { cn } from '@/lib/utils';

interface Job {
  id: number;
  booking_id: number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  service: string;
  service_category?: string;
  address: string;
  postcode: string;
  booking_date: string;
  booking_time_start: string;
  estimated_duration: string;
  total_amount: string;
  notes: string;
  customer_notes: string;
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'rejected';
  created_at: string;
  sla_minutes: number;
  accepted_at?: string;
  started_at?: string;
  completed_at?: string;
}

interface ComplianceItem {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  required: boolean;
  warning?: string;
}

const ProviderActiveJobDetailPage = () => {
  const { jobId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(false);
  const [showScopeAdjustment, setShowScopeAdjustment] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [complianceItems, setComplianceItems] = useState<ComplianceItem[]>([
    { id: 1, title: 'Preventative appliance inspection', description: 'HVAC preventative appliance for indoor environment', completed: true, required: true },
    { id: 2, title: 'Drain water replacement', description: 'Drain water replacement after Codes, Valves/Fittings', completed: true, required: true },
    { id: 3, title: 'Expandable coil cleaning', description: 'Coil cleaning before coil end leak adjustable', completed: false, required: true, warning: 'EXPIRATION SOON' },
    { id: 4, title: 'Drain pan and line inspection', description: 'Inspect drain pan and line for blockages', completed: false, required: false }
  ]);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (jobId && user?.id) {
      fetchJobDetails();
    }
  }, [jobId, user?.id]);

  const fetchJobDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/jobs/${jobId}`);
      if (response.data?.job) {
        setJob(response.data.job);
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      toast({
        title: 'Error',
        description: 'Failed to load job details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkArrived = async () => {
    try {
      // Call the endpoint to start work on the job (transitions from accepted -> in_progress)
      const response = await apiService.post(`/provider/jobs/${jobId}/start`, {});
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Work started! Job is now in progress.',
        });
        fetchJobDetails();
      } else {
        throw new Error(response.message || 'Failed to start work');
      }
    } catch (error) {
      console.error('Error starting work:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start work',
        variant: 'destructive',
      });
    }
  };

  const handleCompleteWork = async () => {
    try {
      const response = await apiService.post(`/provider/jobs/${jobId}/complete`, {});
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Work marked as completed! Job is now complete.',
        });
        fetchJobDetails();
        // Navigate to completed jobs after a short delay
        setTimeout(() => {
          navigate('/provider/completed-jobs');
        }, 1500);
      } else {
        throw new Error(response.message || 'Failed to complete work');
      }
    } catch (error) {
      console.error('Error completing work:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to complete work',
        variant: 'destructive',
      });
    }
  };

  const handleCompleteChecklist = async (itemId: number) => {
    const updatedItems = complianceItems.map(item =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    setComplianceItems(updatedItems);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const uploadedUrls: string[] = [];
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('photo', file);
        const response = await apiService.post('/work-photos/upload-file', formData);
        if (response.success && response.url) {
          uploadedUrls.push(response.url);
          // Save the URL against this job's booking
          if (job?.booking_id) {
            await apiService.post('/work-photos/save', {
              booking_id: job.booking_id,
              photo_url: response.url,
              photo_type: 'completion',
            });
          }
        }
      } catch (err) {
        console.error('Photo upload failed:', err);
        toast({ title: 'Upload Failed', description: `Could not upload ${file.name}`, variant: 'destructive' });
      }
    }
    if (uploadedUrls.length > 0) {
      setUploadedPhotos(prev => [...prev, ...uploadedUrls]);
      toast({ title: 'Photos Uploaded', description: `${uploadedUrls.length} photo(s) uploaded successfully.` });
    }
  };

  if (authLoading || loading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading job details...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!job) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Job not found</p>
            <Button onClick={() => navigate('/provider/available-jobs')}>Back to Jobs</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  const completedCount = complianceItems.filter(i => i.completed).length;
  const completionPercentage = (completedCount / complianceItems.length) * 100;

  return (
    <ProviderLayout>
      <Helmet>
        <title>{job.service} - VireWire Provider</title>
        <meta name="description" content={`Active job: ${job.service}`} />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-cyan-100 text-cyan-700">{job.service_category || 'Service'}</Badge>
                <Badge className={job.status === 'in_progress' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'}>
                  {job.status === 'in_progress' ? 'IN PROGRESS' : 'ACCEPTED'}
                </Badge>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">{job.service}</h1>
              <p className="text-sm text-gray-600 mt-1">
                Created Oct 31, 2023 • Booking ID #{job.booking_id}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">£{Number(job.total_amount).toFixed(2)}</p>
              {job.status === 'accepted' && (
                <Button 
                  className="mt-3 bg-green-600 hover:bg-green-700"
                  onClick={handleMarkArrived}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Start Work
                </Button>
              )}
              {job.status === 'in_progress' && (
                <Button 
                  className="mt-3 bg-orange-600 hover:bg-orange-700"
                  onClick={handleCompleteWork}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Completed
                </Button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-t pt-4">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid grid-cols-6 w-full max-w-2xl">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="photos">Photos</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="checklist">Checklist</TabsTrigger>
                <TabsTrigger value="messages">Messages</TabsTrigger>
                <TabsTrigger value="reports">Reports</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6 mt-6">
                {/* Current Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Current Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                        <div>
                          {job.status === 'accepted' && (
                            <>
                              <p className="font-semibold text-blue-900">Ready to start work</p>
                              <p className="text-sm text-blue-700">Click the button below to begin</p>
                            </>
                          )}
                          {job.status === 'in_progress' && (
                            <>
                              <p className="font-semibold text-blue-900">Work is in progress</p>
                              <p className="text-sm text-blue-700">Complete the checklist and upload photos</p>
                            </>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-blue-600" />
                    </div>

                    {job.status === 'accepted' && (
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700 gap-2 py-6"
                        onClick={handleMarkArrived}
                      >
                        <Check className="w-5 h-5" />
                        Start Work on This Job
                      </Button>
                    )}

                    <Button 
                      variant="outline" 
                      className="w-full gap-2"
                      onClick={() => setShowScopeAdjustment(true)}
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Request Scope Adjustment
                    </Button>
                  </CardContent>
                </Card>

                {/* Site & Access */}
                <Card>
                  <CardHeader>
                    <CardTitle>Site & Access</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-gray-200 rounded-lg h-40 flex items-center justify-center">
                      <div className="text-center">
                        <MapPin className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">Site Map</p>
                      </div>
                    </div>

                    <div className="border-l-4 border-l-primary p-4 bg-gray-50 rounded">
                      <p className="font-semibold text-gray-900 mb-1">📍 {job.address}</p>
                      <p className="text-sm text-gray-600">{job.postcode}</p>
                      <div className="mt-3 space-y-2">
                        <p className="text-sm font-semibold text-gray-700">Site Access Instructions:</p>
                        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                          <li>Enter through the side gate entry</li>
                          <li>Use code #8570 at gate</li>
                          <li>Proceed to the back of premises</li>
                          <li>Contact owner Sarah at +1-555-0147 (after arrival)</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Scope Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle>Scope Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckSquare className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Head Diagnostics</p>
                        <p className="text-sm text-gray-600">Full diagnostics before repair (estimated 30 min)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckSquare className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Required Materials</p>
                        <p className="text-sm text-gray-600">All tools & equipment: Meter, Valves/Fittings</p>
                      </div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-green-900">✓ All prerequisites met for diagnostics complete</p>
                      <p className="text-xs text-green-700 mt-1">Ready to proceed with emergency diagnostics</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Photos Tab */}
              <TabsContent value="photos" className="space-y-6 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Immediate Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {job.status === 'in_progress' && (
                      <Button 
                        className="w-full bg-orange-600 hover:bg-orange-700 gap-2" 
                        size="lg"
                        onClick={handleCompleteWork}
                      >
                        <CheckCircle className="w-5 h-5" />
                        Mark Work as Completed
                      </Button>
                    )}
                    <Button className="w-full bg-green-600 hover:bg-green-700 gap-2" size="lg">
                      <CheckSquare className="w-5 h-5" />
                      Start Completion Checklist
                    </Button>
                    <Button variant="outline" className="w-full gap-2" size="lg">
                      <Camera className="w-5 h-5" />
                      Upload Site Photos
                    </Button>
                    <Button variant="outline" className="w-full gap-2" size="lg">
                      <AlertTriangle className="w-5 h-5" />
                      Request Scope Adjustment
                    </Button>
                  </CardContent>
                </Card>

                {uploadedPhotos.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Captured Today ({uploadedPhotos.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        {uploadedPhotos.map((photo, idx) => (
                          <img 
                            key={idx} 
                            src={photo} 
                            alt={`Photo ${idx + 1}`}
                            className="rounded-lg w-full h-24 object-cover"
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-6 mt-6">
                <Card>
                  <CardContent className="p-6 text-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-600">No documents uploaded yet</p>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Checklist Tab */}
              <TabsContent value="checklist" className="space-y-6 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Compliance Checklist</CardTitle>
                    <CardDescription>
                      {completedCount} of {complianceItems.length} completed
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Progress value={completionPercentage} className="h-2" />
                    
                    <div className="space-y-3">
                      {complianceItems.map(item => (
                        <div 
                          key={item.id}
                          onClick={() => handleCompleteChecklist(item.id)}
                          className={cn(
                            "flex items-start gap-3 p-3 rounded-lg border-l-4 cursor-pointer transition-colors",
                            item.completed 
                              ? "border-l-green-500 bg-green-50" 
                              : item.warning
                              ? "border-l-yellow-500 bg-yellow-50"
                              : "border-l-gray-300 bg-gray-50 hover:bg-gray-100"
                          )}
                        >
                          <CheckSquare 
                            className={cn(
                              "w-5 h-5 mt-0.5 flex-shrink-0",
                              item.completed ? "text-green-600" : "text-gray-400"
                            )}
                          />
                          <div className="flex-1">
                            <p className={cn(
                              "font-semibold",
                              item.completed ? "text-green-900" : "text-gray-900"
                            )}>
                              {item.title}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                            {item.warning && (
                              <Badge variant="outline" className="mt-2 border-yellow-300 bg-yellow-100 text-yellow-700">
                                {item.warning}
                              </Badge>
                            )}
                          </div>
                          {item.completed && <Check className="w-5 h-5 text-green-600" />}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Messages Tab */}
              <TabsContent value="messages" className="space-y-6 mt-6">
                <Card>
                  <CardContent className="p-6 text-center py-12">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-600">No messages yet</p>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Reports Tab */}
              <TabsContent value="reports" className="space-y-6 mt-6">
                <Card>
                  <CardContent className="p-6 text-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-600">No reports generated yet</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2" />
          
          <div className="space-y-6">
            {/* Status Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Job Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Status</span>
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Scheduled</span>
                  <span className="font-medium">{new Date(job.booking_date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Duration</span>
                  <span className="font-medium">{job.estimated_duration}</span>
                </div>
              </CardContent>
            </Card>

            {/* Scope Summary Sidebar */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-green-900">✓ Scope Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-green-800">
                <p>• Preventative maintenance as requested</p>
                <p>• Cleaning of drainage system</p>
                <p>• Verify thermostat calibration and functions</p>
                <p>• Safety inspection and documentation</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Scope Adjustment Modal */}
      <Dialog open={showScopeAdjustment} onOpenChange={setShowScopeAdjustment}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Scope Adjustment</DialogTitle>
            <DialogDescription>
              Submit for platform review. VireWire must review and approve from the client or Focus VireWire management requests.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Scope Summary */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Scope Summary</Label>
              <Textarea 
                placeholder="Description of the scope change needed..."
                className="min-h-[100px]"
                defaultValue="Extraction of a HVAC filter due to accumulation of secondary cleaning. Fixed has resulted in gas/oil mist at the outdoor of 3-4 hours."
              />
            </div>

            {/* Item Category */}
            <div className="space-y-2">
              <Label htmlFor="category" className="text-base font-semibold">Item Category</Label>
              <select 
                id="category"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option>Additional labour required</option>
                <option>Materials shortage</option>
                <option>Safety concern</option>
              </select>
            </div>

            {/* Cost Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="original">Original Cost</Label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <Input id="original" type="number" className="pl-6" defaultValue="0.00" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="additional">Additional Cost</Label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <Input id="additional" type="number" className="pl-6" defaultValue="0" />
                </div>
              </div>
            </div>

            {/* High Urgency Toggle */}
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-800">High urgency</AlertTitle>
              <AlertDescription className="text-red-700">
                This will exceed the original estimate/value
              </AlertDescription>
              <div className="mt-3">
                <input type="checkbox" id="urgency" className="mr-2" />
                <label htmlFor="urgency" className="text-sm text-red-700">Mark as high urgency</label>
              </div>
            </Alert>

            {/* Evidence Upload */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Upload Evidence</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400">
                <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Drag photos here or click to browse</p>
              </div>
            </div>

            {/* Confirmation */}
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Yes, I can continue other job deliverables</AlertTitle>
            </Alert>

            {/* Buttons */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowScopeAdjustment(false)}>
                Cancel
              </Button>
              <Button className="bg-green-600 hover:bg-green-700">
                Submit for Platform Review
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
};

export default ProviderActiveJobDetailPage;
